import { useEffect, useRef, useState } from 'react'
import { plan_top_down_nodes_path } from '@/lib/routes'
import { type PlanNode } from '../components/PlanNodesPanel'

export function serializeNodes(nodes: PlanNode[]): Array<Record<string, unknown>> {
  return nodes.map((n) => ({
    id:              n.id,
    name:            n.name,
    short_name:      n.shortName ?? null,
    special:         n.special ?? false,
    is_new_product:  n.isNewProduct ?? false,
    target_revenue:  n.targetRevenue ?? Number(n.revenue) ?? 0,
    product_node_id: n.productNodeId ?? null,
    children:        serializeNodes(n.children)
  }))
}

// The editor's node tree as state, re-synced whenever the server sends a
// fresh `serverNodes` (period change reloads), and autosaved (debounced)
// on every local change. A render right after a server sync is not saved —
// nothing changed. `versionId` is the draft the page loaded; the server
// refuses the save with a "reload" alert when that draft is gone (the
// revision was approved or discarded in another tab).
export function useNodesAutosave({
  marketSlug,
  slug,
  serverNodes,
  versionId,
}: {
  marketSlug: string
  slug: string
  serverNodes: PlanNode[]
  versionId: string | null
}) {
  const [planNodes, setPlanNodes] = useState<PlanNode[]>(serverNodes)
  const hydratedFromServer = useRef(true)

  useEffect(() => {
    setPlanNodes(serverNodes)
    hydratedFromServer.current = true
  }, [serverNodes])

  useEffect(() => {
    if (hydratedFromServer.current) {
      hydratedFromServer.current = false
      return
    }
    const handle = window.setTimeout(() => {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
      fetch(plan_top_down_nodes_path(marketSlug, slug), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf,
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ version_id: versionId, nodes: serializeNodes(planNodes) })
      }).then(async (res) => {
        if (res.ok) return
        const data = await res.json().catch(() => ({ error: 'Failed to save.' }))
        window.alert(data.error ?? 'Failed to save.')
      })
    }, 500)
    return () => window.clearTimeout(handle)
  }, [marketSlug, slug, planNodes, versionId])

  return [planNodes, setPlanNodes] as const
}
